import axios from "axios";
import { API_URL } from "../constants";
import { ChatMessage, Conversation, ChatReader, MessageReaction } from "../types/Chat";

export async function fetchConversations(companyId: string): Promise<Conversation[]> {
  const res = await axios.get(`${API_URL}/${companyId}/conversations`);
  return (res.data?.conversations || []) as Conversation[];
}

export async function startDm(
  companyId: string,
  userId: string
): Promise<Conversation> {
  const res = await axios.post(`${API_URL}/${companyId}/conversations/dm`, {
    userId,
  });
  return res.data.conversation as Conversation;
}

export interface MessagesPage {
  messages: ChatMessage[];
  hasMore: boolean;
  nextCursor: number | null;
}

export async function fetchMessages(
  companyId: string,
  conversationId: string,
  options: { cursor?: number; limit?: number } = {}
): Promise<MessagesPage> {
  const params = new URLSearchParams();
  if (options.cursor !== undefined) params.set("cursor", String(options.cursor));
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  const qs = params.toString();
  const url = `${API_URL}/${companyId}/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`;
  const res = await axios.get(url);
  return res.data as MessagesPage;
}

export async function sendMessage(
  companyId: string,
  conversationId: string,
  payload: { content?: string; fileId?: string }
): Promise<ChatMessage> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/messages`,
    payload
  );
  return res.data.message as ChatMessage;
}

export async function editMessage(
  companyId: string,
  messageId: string,
  content: string
): Promise<ChatMessage> {
  const res = await axios.patch(`${API_URL}/${companyId}/messages/${messageId}`, {
    content,
  });
  return res.data.message as ChatMessage;
}

export async function deleteMessage(companyId: string, messageId: string) {
  await axios.delete(`${API_URL}/${companyId}/messages/${messageId}`);
}

export async function markConversationRead(
  companyId: string,
  conversationId: string
): Promise<{ lastReadAt: number }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/read`
  );
  return res.data;
}

/**
 * Fetch the list of users who have read past `messageCreatedAt` in a
 * conversation. Used by the "Viewed by" popover for company-chat messages —
 * loaded lazily on hover, not bundled with the messages or conversations
 * payloads (the participants table for company chat can be hundreds of rows).
 */
export async function fetchReaders(
  companyId: string,
  conversationId: string,
  messageCreatedAt: number
): Promise<ChatReader[]> {
  const res = await axios.get(
    `${API_URL}/${companyId}/conversations/${conversationId}/readers`,
    { params: { after: messageCreatedAt } }
  );
  return (res.data?.readers || []) as ChatReader[];
}

/**
 * Telegram-style reaction toggle. The server resolves what to do based on
 * the user's existing reaction:
 *   - none           → INSERT
 *   - same emoji     → DELETE (toggle off)
 *   - different emoji → UPDATE (switch)
 * Returns the freshly-aggregated reactions array for the affected message.
 */
export async function addReaction(
  companyId: string,
  messageId: string,
  emoji: string
): Promise<MessageReaction[]> {
  const res = await axios.post(
    `${API_URL}/${companyId}/messages/${messageId}/react`,
    { emoji }
  );
  return (res.data?.reactions || []) as MessageReaction[];
}

/**
 * Explicit "remove my reaction" — used when the user clicks their own
 * reaction pill to dismiss it. Idempotent on the server.
 */
export async function removeReaction(
  companyId: string,
  messageId: string
): Promise<MessageReaction[]> {
  const res = await axios.delete(
    `${API_URL}/${companyId}/messages/${messageId}/react`
  );
  return (res.data?.reactions || []) as MessageReaction[];
}

export interface CompanyUnreadEntry {
  count: number;
  name: string;
}

export async function fetchUnreadByCompany(): Promise<Record<string, CompanyUnreadEntry>> {
  const res = await axios.get(`${API_URL}/me/unread-by-company`);
  return res.data?.unreadByCompany || {};
}
